import {Component, ErrorHandler, Inject, OnDestroy, OnInit, Optional, Renderer2, SecurityContext} from '@angular/core';
import {ApplicationInsightsService} from './shared/services/application-insights.service';
import {MsalBroadcastService as BroadcastService, MsalGuardConfiguration, MSAL_GUARD_CONFIG} from '@azure/msal-angular';
import {Subscription, of, timer} from 'rxjs';
import {UserService} from './shared/services/user.service';
import {LoaderService} from './shared/services/loader.service';
import {EnvService} from './env.service';
import FontFaceObserver from 'fontfaceobserver';
import {UserModel} from './shared/models/user.model';
import moment from 'moment';
import {UserSettingsService} from './shared/services/user-settings.service';
import {distinctUntilChanged, filter, map, mapTo, switchMap} from 'rxjs/operators';
import {environment} from 'src/environments/environment';
import {UserTheme} from 'src/app/shared/models/user.model';
import {InteractionRequiredAuthError} from 'msal';
import {RedirectRequest} from '@azure/msal-browser';
import {PitMsalService} from './shared/services/pit-msal.service';
import memoize from 'lodash/memoize';
import {NewVersionService} from './version/new-version/service/new-version.service';
import {MessageService} from './shared/services/message-service.service';
import {TranslatePipe} from '@ngx-translate/core';
import {DomSanitizer} from '@angular/platform-browser';
import {PdftronKeyService} from './po-approval-manager/services/pdf-config.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [ApplicationInsightsService]
})
export class AppComponent implements OnInit, OnDestroy {
  readonly REFRESH_MESSAGE = 30000;
  public loggedIn = false;
  public allowLogout = false;
  private loginSubscription: Subscription;
  private logoutSubscription: Subscription;
  public displayName: string;
  public authUserName: string;
  public user: UserModel;
  public userDetails$;
  public isLoader = false;
  public isMimicMode: boolean;
  public year = moment().format('YYYY');

  // tslint:disable-next-line:no-unused-variable
  constructor(private appInsightsService: ApplicationInsightsService,
              private broadcastService: BroadcastService,
              private authService: PitMsalService,
              private errorHandler: ErrorHandler,
              private userService: UserService,
              private userSettingsService: UserSettingsService,
              private loaderService: LoaderService,
              private envService: EnvService,
              private renderer: Renderer2,
              private newVersionService: NewVersionService,
              private messageService: MessageService,
              private sanitizer: DomSanitizer,
              private translate: TranslatePipe,
              private pdftronKeyService: PdftronKeyService,
              @Optional() @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration) {
    this.userDetails$ = this.userService.getUserDetails$();

    const materialIcons = new FontFaceObserver('Material Icons');
    materialIcons.load(null, 10000)
      .then(() => this.renderer.addClass(document.body, `material-icons-loaded`))
      .catch(() => this.renderer.addClass(document.body, `material-icons-error`));
  }

  getScrollbarWidth = memoize(() => {
    // Creating invisible container
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll'; // forcing scrollbar to appear
    (outer.style as any).msOverflowStyle = 'scrollbar'; // needed for WinJS apps
    document.body.appendChild(outer);

    // Creating inner element and placing it in the container
    const inner = document.createElement('div');
    outer.appendChild(inner);

    // Calculating difference between container's full width and the child width
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

    // Removing temporary elements from the DOM
    outer.parentNode.removeChild(outer);

    return scrollbarWidth + 'px';
  });
  ngOnInit() {
    this.checkAccount();

    this.loginSubscription = this.broadcastService.msalSubject$.pipe(
      filter((e: any) => e.eventType === 'msal:loginSuccess'),
      map((e: any) => e.error)
    ).subscribe(() => {
      this.checkAccount();
    });
    let logoutErrorsCount = 0;
    this.logoutSubscription = this.broadcastService.msalSubject$.pipe(
      filter((e: any) => e.eventType === 'msal:acquireTokenFailure'),
      map((e: any) => e.error)
    ).subscribe((error) => {
      console.warn(error);
      if (error instanceof InteractionRequiredAuthError && logoutErrorsCount === 0) {
        logoutErrorsCount++;
        return;
      }
      this.logoutSubscription.unsubscribe();
      if (this.msalGuardConfig?.authRequest) {
        this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
      } else {
        this.authService.loginRedirect();
      }
    });

    this.authService.handleRedirectObservable(window.location.hash).subscribe({
      error: (authError) => {
        if (authError) {
          this.allowLogout = true;
          this.errorHandler.handleError(authError);
        }
      }
    });

    this.loaderService.spinnerStatus.subscribe(async state => {
      await Promise.resolve();
      this.isLoader = state;
    });

    this.checkMimic();
    this.trackNewVersionChanges();
  }

  login() {
    this.allowLogout = false;

    if (this.msalGuardConfig?.authRequest) {
      this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  logout() {
    this.userService.resetUser();
    this.authLogout();
  }

  authLogout() {
    this.authService.logout();
  }

  checkAccount() {
    this.loggedIn = !!this.authService.getAccount();
    if (!this.loggedIn) {
      return;
    }
    this.pdftronKeyService.loadPdfLicense(); //Get PDFTron key once user authenticated
    this.displayName = this.authService.getAccount()?.name;
    const currentUserName = this.userService.getCurrentUserName();
    if (!currentUserName) {
      this.setAuthUserName();
    } else {
      this.userService.setCurrentUserName(currentUserName);
    }

    this.userSettingsService.initiate();
    const userTheme$ = this.userSettingsService.userTheme$;
    userTheme$.pipe(
      distinctUntilChanged(),
    ).subscribe((userTheme) => {
      if (userTheme === UserTheme.DARK) {
        if (document.querySelector('[id="dark-mode-styles"]') == null) {
          const link = document.createElement( 'link' );
          link.href = 'dark-mode.css?v=' + environment.appVersion;
          link.type = 'text/css';
          link.rel = 'stylesheet';
          link.media = 'screen';
          link.id = 'dark-mode-styles';

          const head = document.getElementsByTagName( 'head' )[0];
          const primaryStyles = head.querySelector('[href*="styles"]');
          try {
            head.insertBefore(link, primaryStyles.nextSibling);
          } catch (e) {
            head.appendChild(link);
          }
        }
      } else if(document.querySelector('[id="dark-mode-styles"]')) {
        document.querySelector('[id="dark-mode-styles"]').remove();
      }
      document.body.setAttribute('data-theme', userTheme.toLowerCase());
    });

    const userName1 = this.authService.getAccount()?.username;
    if (userName1) {
      const authUserName = userName1.substring(0, userName1.indexOf('@'));
      this.authUserName = authUserName;
    }

    this.checkMimic();
  }

  resetMimicUser(): void {
    this.setAuthUserName();
    window.location.reload();
  }

  private checkMimic(): void {
    const realUserEmail = this.authService.getAccount()?.username;
    this.isMimicMode = this.userService.isMimicMode(realUserEmail);
  }

  private setAuthUserName(): void {
    const userName1 = this.authService.getAccount()?.username;
    this.userService.setCurrentUserName(userName1.substring(0, userName1.indexOf('@')));
  }

  private trackNewVersionChanges(): void {
    this.newVersionService
      .observe$()
      .pipe(
        switchMap(hasNewVersion =>
          hasNewVersion ? timer(0, this.REFRESH_MESSAGE).pipe(mapTo(hasNewVersion)) : of(hasNewVersion),
        ),
      )
      .subscribe(hasNewVersion => {
        if (hasNewVersion) {
          console.log('New version available.');
          this.messageService.showSystemMessage('warning',
          this.translate.transform('common.warning.version-update-header'),
          this.sanitizer.bypassSecurityTrustHtml(
            this.sanitizer.sanitize(SecurityContext.NONE, this.translate.transform('common.warning.version-update-desc'))
          ));
        }
      });
  }

  ngOnDestroy(): void {
    this.loginSubscription?.unsubscribe();
    this.logoutSubscription?.unsubscribe();
  }

}
